import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import {
  Cover,
  H1,
  color,
  Image,
  radius,
  space,
  Text,
  device,
  fontWeight,
  Button,
  shadow,
  H3,
  Pill,
} from '@ticketswap/solar'
import styled from '@emotion/styled'
import { useLanguage } from '~/components/LanguageContext'
import { getDateTimeString } from '~/utils/event'
import Link from 'next/link'
import { getLinkProps } from '~/utils'
import { decodeGlobalId } from '~/utils/id'
import { ChevronLeft } from '@ticketswap/comets'
import { eventFragmentPropTypes } from '~/graphql/fragments/event'
import visit from '~/utils/visit'

const Back = styled(ChevronLeft)`
  margin-right: ${space[8]};
`

const BackButton = styled(Pill)`
  margin-top: ${space[24]};
  text-transform: unset;
  font-weight: ${fontWeight.semiBold};

  @media ${device.tablet} {
    display: none;
  }
`

const Header = styled.header`
  padding: 0 ${space[16]};
  display: ${props => (props.hasNavigationActions ? 'none' : 'block')};
  position: relative;
  z-index: 1;

  @media ${device.tablet} {
    display: grid;
    grid-column-gap: ${space[16]};
    grid-template-columns: auto 704px auto;
    grid-template-areas: 'left image right' 'left title right' 'left date right' 'left location right' 'left navigation right';
    box-shadow: ${shadow.light};
    margin-bottom: ${space[32]};
    padding: unset;

    [data-theme='dark'] & {
      border-bottom: 1px solid ${color.spaceLightest};
    }
  }
`

const Title = styled(H1)`
  margin-top: ${space[16]};

  @media ${device.tablet} {
    grid-area: title;
    color: ${color.nova};
    margin-top: unset;

    [data-theme='dark'] & {
      color: ${color.space};
    }
  }
`

const ListingTitle = styled(H3)`
  margin-top: ${space[8]};

  @media ${device.tablet} {
    display: none;
  }
`

const ListingSubtitle = styled(Text)`
  margin-top: ${space[8]};
  margin-bottom: ${space[24]};
  color: ${color.spaceMedium};

  @media ${device.tablet} {
    display: none;
  }
`

const LocationText = styled(Text)`
  color: ${color.spaceMedium};
  margin-top: ${space[8]};

  a {
    color: ${color.spaceMedium};
  }

  @media ${device.tablet} {
    grid-area: location;
    margin-bottom: ${space[48]};
    color: ${color.nova};

    [data-theme='dark'] & {
      color: ${color.space};
    }

    a {
      color: ${color.nova};

      [data-theme='dark'] & {
        color: ${color.space};
      }
    }
  }
`

const DateText = styled(Text)`
  display: none;

  @media ${device.tablet} {
    display: block;
    grid-area: date;
    color: ${color.nova};
    margin-top: ${space[16]};

    [data-theme='dark'] & {
      color: ${color.space};
    }
  }
`

const EventImage = styled(Image)`
  margin-left: -${space[16]};
  margin-right: -${space[16]};
  z-index: -1;

  &::after {
    z-index: 2;
    content: '';
    display: block;
    position: absolute;
    right: 0;
    left: 0;
    bottom: 0;
    height: 50%;
    background-image: linear-gradient(
      to top,
      rgba(0, 19, 25, 1) 0%,
      rgba(0, 19, 25, 0.738) 19%,
      rgba(0, 19, 25, 0.541) 34%,
      rgba(0, 19, 25, 0.382) 47%,
      rgba(0, 19, 25, 0.278) 56.5%,
      rgba(0, 19, 25, 0.194) 65%,
      rgba(0, 19, 25, 0.126) 73%,
      rgba(0, 19, 25, 0.075) 80.2%,
      rgba(0, 19, 25, 0.042) 86.1%,
      rgba(0, 19, 25, 0.021) 91%,
      rgba(0, 19, 25, 0.008) 95.2%,
      rgba(0, 19, 25, 0.002) 98.2%,
      rgba(0, 19, 25, 0) 100%
    );
  }

  @media ${device.tablet} {
    grid-area: image;
    width: 254px;
    height: 143px;
    border-radius: ${radius.md};
    margin-bottom: ${space[32]};
    margin-left: unset;
    margin-right: unset;
    margin-top: ${space[96]};
    z-index: unset;

    &::after {
      background-image: unset;
    }
  }
`

const StyledCover = styled(Cover)`
  display: none;

  @media ${device.tablet} {
    z-index: -1;
    display: block;
    grid-column-start: left;
    grid-column-end: right;
    grid-row-start: image;
    grid-row-end: location;
  }
`

const ActionNavigation = styled.div`
  display: none;

  @media ${device.tablet} {
    grid-area: navigation;
    align-self: center;
    padding-top: ${space[16]};
    padding-bottom: ${space[16]};
    display: flex;
  }
`
const ActionNavigationBack = styled(Button)`
  padding: 0 ${space[16]};

  > span:first-of-type {
    margin-left: unset;
    margin-right: unset;
  }
`

const ActionNavigationTitles = styled.div`
  margin-left: ${space[16]};
  align-self: center;
`

const ActionNavigationTitle = styled(H3)`
  @media ${device.tablet} {
    font-weight: ${fontWeight.semiBold};
    font-size: 22px;
  }
`
const ActionNavigationSubtitle = styled(Text)`
  @media ${device.tablet} {
    color: ${color.spaceMedium};
    font-size: 14px;
  }
`

const ListingHeader = ({ t, coverImage, event, eventType, listing }) => {
  const language = useLanguage()
  const { name, startDate, endDate, location } = event
  const { numberOfTicketsStillForSale } = listing

  const listingTitle =
    numberOfTicketsStillForSale === 0
      ? eventType.title
      : t(`listing:header.title${numberOfTicketsStillForSale > 1 ? '_plural' : ''}`, {
          count: numberOfTicketsStillForSale,
          eventType: eventType.title,
        })
  const listingSubtitle = t('listing:header.subtitle', {
    date: getDateTimeString({ startDate, endDate }, language),
  })

  const returnToEventTypePage = () =>
    visit(
      getLinkProps('EVENT_TYPE_PAGE', {
        eventId: decodeGlobalId(event.id),
        eventSlug: event.slug,
        eventTypeSlug: eventType.slug,
        eventTypeId: decodeGlobalId(eventType.id),
      })
    )

  return (
    <Header>
      <StyledCover blurred imageUrl={coverImage} />
      <EventImage src={coverImage} alt={name} />

      <BackButton variant="secondary" onClick={returnToEventTypePage}>
        <Back size={14} /> {t('listing:header.back')}
      </BackButton>

      <Title>{name}</Title>
      <ListingTitle>{listingTitle}</ListingTitle>
      <DateText as="p">{getDateTimeString({ startDate, endDate }, language)}</DateText>
      <LocationText as="p">
        <Link
          {...getLinkProps('LOCATION', {
            slug: location.slug,
            id: decodeGlobalId(location.id),
          })}
        >
          <a>{location.name}</a>
        </Link>
        {location.city && (
          <>
            {', '}
            <Link
              {...getLinkProps('CITY', {
                slug: location.city.slug,
                id: decodeGlobalId(location.city.id),
              })}
            >
              <a>{location.city.name}</a>
            </Link>
          </>
        )}
        {location.country && `, ${location.country.name}`}
      </LocationText>
      <ListingSubtitle as="p">{listingSubtitle}</ListingSubtitle>

      <ActionNavigation>
        <ActionNavigationBack
          variant="secondary"
          onClick={returnToEventTypePage}
          leftAdornment={<ChevronLeft size={24} />}
        />

        <ActionNavigationTitles>
          <ActionNavigationTitle>{listingTitle}</ActionNavigationTitle>
          <ActionNavigationSubtitle as="p">{listingSubtitle}</ActionNavigationSubtitle>
        </ActionNavigationTitles>
      </ActionNavigation>
    </Header>
  )
}

ListingHeader.propTypes = {
  t: PropTypes.func.isRequired,
  coverImage: PropTypes.string.isRequired,
  event: eventFragmentPropTypes.isRequired,
  eventType: PropTypes.shape({
    title: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
  }).isRequired,
}

export default ListingHeader
